import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'Services/APIService';
import { requestCache } from '../../utils/requestCache';

// Units Purchase Interfaces
interface PurchaseUnitsPayload {
  groupId: string;
  serviceId: string;
  investorsEmailId: string;
  investorName: string;
  investorAvatar: string;
  purchaseUnits: string;
  totalInvestmentAmt: string;
  subscriptionType: string;
  unitPrice?: string;
  operationCharge?: string;
}

interface PurchaseUnitsResponse {
  message: string;
  requestId: string;
  unitType: string;
  investorEmailId: string;
  purchaseUnits: string;
  totalInvestmentAmount: string;
  submissionDateTime: string;
}

// Units Request Management Interfaces
interface PurchaseRequest {
  requestId: string;
  unitType: string;
  investorEmailId: string;
  investorName: string;
  investorAvatar: string;
  investedUnits: string;
  investedAmount: string;
  unitPrice: string;
  date_str?: string;
  status: 'pending' | 'accepted' | 'rejected';
  createdAt: string;
  lastUpdatedDateTime: string;
  lastUpdatedBy: string;
}

interface GetPurchaseRequestsPayload {
  groupId: string;
  serviceId: string;
  unitType: string;
  filters?: {
    investorEmailId?: string;
  };
  pageSize: number;
  continuationToken: string | null;
}

interface GetPurchaseRequestsResponse {
  purchaseRequests: PurchaseRequest[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  nextToken: string | null;
  hasMore: boolean;
}

interface AcceptRejectUnitsPayload {
  groupId: string;
  serviceId: string;
  unitType: string;
  requestId: string;
  date_str: string;
  fromMarketplace: boolean;
  action: 'accepted' | 'rejected';
}


interface AcceptRejectUnitsResponse {
  success: boolean;
  message: string;
  requestId: string;
  action: string;
}

// Asset Purchase Interfaces
interface MonthlyIncome {
  primary: string;
  secondary: string;
  other: string;
  totalGrossIncome: string;
}

interface MonthlyExpenses {
  financing: string;
  management: string;
  operational: string;
  fees: string;
  totalExpenses: string;
}

interface Liability {
  type: string;
  provider: string;
  amount: string;
  rate: string;
  monthlyPayment: string;
}

interface AssetData {
  type: string;
  subType: string;
  identifier: string;
  description: string;
  purchaseValue: string;
  cashUsed: string;
  expectedAnnualYield: string;
  monthlyIncome: MonthlyIncome;
  monthlyExpenses: MonthlyExpenses;
  netMonthlyIncome: string;
  liabilities: Liability[];
  additionalInfo: any;
  // Growth-specific fields
  currentValue?: string;
  quantity?: string;
  maturityDate?: string;
}

interface PurchaseAssetPayload {
  groupId: string;
  serviceId: string;
  unitType: 'incomeUnits' | 'growthUnits';
  asset: AssetData;
}

interface PurchaseAssetResponse {
  message: string;
  requestId: string;
  unitType: string;
  submissionDateTime: string;
}

// Asset Request Management Interfaces
interface AssetRequest {
  requestId: string;
  unitType: string;
  status: 'pending' | 'accepted' | 'rejected';
  createdAt: string;
  lastUpdatedDateTime: string;
  lastUpdatedBy: string;
  asset: AssetData;
  fundManager: string;
}

interface GetAssetRequestsPayload {
  groupId: string;
  serviceId: string;
  unitType: string;
  pageSize: number;
  continuationToken: string | null;
}

interface GetAssetRequestsResponse {
  purchaseAssetRequests: AssetRequest[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  nextToken: string | null;
  hasMore: boolean;
}

interface AcceptRejectAssetPayload {
  groupId: string;
  serviceId: string;
  unitType: string;
  requestId: string;
  action: 'accepted' | 'rejected';
}

interface AcceptRejectAssetResponse {
  success: boolean;
  message: string;
}

// Asset List Interfaces
interface Asset {
  assetId: string;
  type: string;
  assetData: AssetData;
  cashUsed: string;
  currentValue?: string;
  purchaseDate: string;
  fundManager: string;
  sellValue?: string;
  isSold?: boolean;
}

interface GetAllAssetsPayload {
  groupId: string;
  serviceId: string;
  unitType: string;
  pageSize: number;
  continuationToken: string | null;
}

interface GetAllAssetsResponse {
  assets: Asset[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  nextToken: string | null;
  hasMore: boolean;
}

// Landing Data Interfaces
interface LandingData {
  unitType: string;
  amountInvestedTillDate: string;
  totalUnitsHoldTillDate: string;
  totalMonthlyIncome: string;
  totalMonthlyExpense: string;
  totalInvestorsCount: number;
  totalCashUsed: string;
  createdDateTime: string;
  lastUpdatedDateTime: string;
  createdBy: string;
  lastmodifiedBy: string;
  unitPrice: string;
  NAV: string;
  totalPropertiesCurrentValue?: string;
  cashRemaining?: string;
  operationCharge?: string;
  unitPriceSeries?: Array<{
    x: string;
    y?: number;
    unitPrice?: number;
    averageUnitPrice?: number;
  }>;
}

interface GetLandingDataPayload {
  groupId: string;
  serviceId: string;
  isAdmin: boolean;
  range?: '1W' | '1M' | '3M' | '5M' | '6M' | '1Y' | '2Y' | '5Y';
  unitType?: 'incomeUnits' | 'growthUnits';
  unitTypes?: Array<'incomeUnits' | 'growthUnits'>;
  includeGraph?: boolean;
  includeRecords?: boolean;
  fromDate?: string;
  toDate?: string;
  fromDateTime?: string | null;
  toDateTime?: string | null;
}

// NEW: Investor Landing Data Interfaces
interface InvestorUnitsData {
  unitType: string;
  investorName: string;
  investorsEmailId: string;
  amountInvestedTillDate: string;
  totalUnitsHoldTillDate: string;
  createdDateTime: string;
  lastUpdatedDateTime: string;
  createdBy: string;
  lastmodifiedBy: string;
  id?: string;
  investorAvatar?: string;
  monthlyIncome?: string; // Only for incomeUnits
}

interface InvestorLandingDataResponse {
  incomeUnits: InvestorUnitsData;
  growthUnits: InvestorUnitsData;
}

interface GetInvestorLandingDataPayload {
  groupId: string;
  serviceId: string;
  investorsEmailId?: string;
  isAdmin?: boolean;
  range?: '1W' | '1M' | '3M' | '5M' | '6M' | '1Y' | '2Y' | '5Y';
  includeGraph?: boolean;
  includeRecords?: boolean;
  unitType?: 'incomeUnits' | 'growthUnits';
  unitTypes?: Array<'incomeUnits' | 'growthUnits'>;
  fromDate?: string;
  toDate?: string;
  fromDateTime?: string | null;
  toDateTime?: string | null;
  maxGraphPoints?: number;
}

interface LandingDataApiResponse {
  data?: {
    analysis?: {
      incomeUnits?: LandingData;
      growthUnits?: LandingData;
    };
    graph?: {
      incomeUnits?: {
        series?: {
          unitPrice?: Array<{
            x: string;
            y?: number;
            unitPrice?: number;
            averageUnitPrice?: number;
          }>;
        };
      };
      growthUnits?: {
        series?: {
          unitPrice?: Array<{
            x: string;
            y?: number;
            unitPrice?: number;
            averageUnitPrice?: number;
          }>;
        };
      };
    };
  };
}

interface InvestorLandingDataApiResponse {
  data?: {
    incomeUnits?: InvestorUnitsData;
    growthUnits?: InvestorUnitsData;
    investor?: {
      incomeUnits?: InvestorUnitsData;
      growthUnits?: InvestorUnitsData;
    };
    investorLandingData?: {
      incomeUnits?: InvestorUnitsData;
      growthUnits?: InvestorUnitsData;
    };
  };
  incomeUnits?: InvestorUnitsData;
  growthUnits?: InvestorUnitsData;
}

const normalizeLandingDataResponse = (raw: any): LandingData[] => {
  if (Array.isArray(raw)) {
    return raw;
  }

  const response = raw as LandingDataApiResponse;
  const analysis = response?.data?.analysis;
  if (!analysis) {
    return [];
  }

  const normalized: LandingData[] = [];
  if (analysis.incomeUnits) {
    const incomeSeries = response?.data?.graph?.incomeUnits?.series?.unitPrice || [];
    normalized.push({
      ...analysis.incomeUnits,
      unitPriceSeries: incomeSeries
    });
  }
  if (analysis.growthUnits) {
    const growthSeries = response?.data?.graph?.growthUnits?.series?.unitPrice || [];
    normalized.push({
      ...analysis.growthUnits,
      unitPriceSeries: growthSeries
    });
  }
  return normalized;
};

const emptyInvestorUnits: InvestorUnitsData = {
  unitType: '',
  investorName: '',
  investorsEmailId: '',
  amountInvestedTillDate: '0',
  totalUnitsHoldTillDate: '0',
  createdDateTime: '',
  lastUpdatedDateTime: '',
  createdBy: '',
  lastmodifiedBy: ''
};

const normalizeInvestorLandingDataResponse = (raw: any): InvestorLandingDataResponse => {
  const response = raw as InvestorLandingDataApiResponse;

  const topLevelIncome = response?.incomeUnits;
  const topLevelGrowth = response?.growthUnits;
  const dataIncome = response?.data?.incomeUnits;
  const dataGrowth = response?.data?.growthUnits;
  const dataInvestorIncome = response?.data?.investor?.incomeUnits;
  const dataInvestorGrowth = response?.data?.investor?.growthUnits;
  const nestedIncome = response?.data?.investorLandingData?.incomeUnits;
  const nestedGrowth = response?.data?.investorLandingData?.growthUnits;

  return {
    incomeUnits:
      topLevelIncome ||
      dataIncome ||
      dataInvestorIncome ||
      nestedIncome ||
      { ...emptyInvestorUnits },
    growthUnits:
      topLevelGrowth ||
      dataGrowth ||
      dataInvestorGrowth ||
      nestedGrowth ||
      { ...emptyInvestorUnits }
  };
};

// Asset Revaluation Interfaces
interface UpdatedMonthlyExpenses {
  financing: string;
  management: string;
  operational: string;
  fees: string;
  totalExpenses: string;
}

interface UpdatedMonthlyIncome {
  primary: string;
  secondary: string;
  other: string;
  totalGrossIncome: string;
}

interface RevaluationAssetData {
  assetId: string;
  revaluedCurrentValue?: string;
  updatedMonthlyIncome?: UpdatedMonthlyIncome;
  updatedMonthlyExpenses?: UpdatedMonthlyExpenses;
}

interface RevaluateAssetPayload {
  groupId: string;
  serviceId: string;
  unitType: 'incomeUnits' | 'growthUnits';
  asset: Asset;
}

interface RevaluateAssetResponse {
  success: boolean;
  message: string;
  assetId: string;
  updatedDateTime: string;
}

// Asset Revaluation Request Interfaces
interface RevaluationRequest {
  requestId: string;
  groupId: string;
  serviceId: string;
  unitType: string;
  asset: {
    assetId: string;
    type: string;
    assetData: AssetData;
    cashUsed: string;
    currentValue: string;
    purchaseDate: string;
    fundManager: string;
  };
  status: 'pending' | 'accepted' | 'rejected';
  createdBy: string;
  createdDate: string;
  lastUpdatedBy: string;
  lastUpdatedDate: string;
}

interface GetRevaluationRequestsPayload {
  groupId: string;
  serviceId: string;
  unitType: string;
  pageSize: number;
  continuationToken: string | null;
}

interface GetRevaluationRequestsResponse {
  revaluationAssetRequests: RevaluationRequest[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  nextToken: string | null;
  hasMore: boolean;
  filters: object;
}

interface AcceptRejectRevaluationPayload {
  groupId: string;
  serviceId: string;
  unitType: string;
  requestId: string;
  action: 'accepted' | 'rejected';
}

interface AcceptRejectRevaluationResponse {
  success: boolean;
  message: string;
  requestId: string;
  action: string;
}

interface Pagination {
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
  nextToken: string | null;
}

// Contribution Request Interfaces
interface ContributionRequest {
  requestId: string;
  unitType: string;
  investorEmailId: string;
  investorName: string;
  investorAvatar: string;
  investedUnits: string;
  investedAmount: string;
  unitPrice: string;
  status: 'pending' | 'accepted' | 'rejected';
  createdAt: string;
  lastUpdatedDateTime: string;
  lastUpdatedBy: string;
}

interface GetContributionRequestsPayload {
  groupId: string;
  serviceId: string;
  unitType: 'growthUnits' | 'incomeUnits';
  frequency: string;
  filters?: {
    fromDate?: string;
    toDate?: string;
    status?: string;
  };
  pageSize: number;
  continuationToken: string | null;
}

interface GetContributionRequestsResponse {
  contributionRequests: ContributionRequest[];
  totalCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
  nextToken: string | null;
  hasMore: boolean;
  filters: object;
}

// Sell Units Interfaces
interface SellUnitsPayload {
  groupId: string;
  serviceId: string;
  investorsEmailId: string;
  investorName: string;
  investorAvatar: string;
  sellUnits: string;
  unitType: string;
  unitPrice: string;
  operationCharge?: string;
  operationalPercentage?: number;
  netAmount?: string;
}

interface SellUnitsResponse {
  status: string;
  message: string;
}

// Sell Units Request Management Interfaces
interface SellRequest {
  requestId: string;
  unitType: string;
  investorEmailId: string;
  investorName: string;
  investorAvatar: string;
  sellUnits: string;
  unitPrice: string;
  operationCharge: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  lastUpdatedDateTime: string;
  lastUpdatedBy: string;
  requestDate: string;
}

interface GetSellRequestsPayload {
  groupId: string;
  serviceId: string;
  unitType: string;
  pageSize: number;
  continuationToken: string | null;
}

interface GetSellRequestsResponse {
  sellRequests: SellRequest[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  nextToken: string | null;
  hasMore: boolean;
  filters: object;
}

// State Interface
interface FundManagementInvestmentState {
  // Units Purchase
  purchaseRequests: PurchaseRequest[];
  unitsPurchaseStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  unitsAcceptRejectStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  lastUnitsPurchase: PurchaseUnitsResponse | null;
  lastUnitsProcessed: AcceptRejectUnitsResponse | null;

  // Asset Purchase
  assetRequests: AssetRequest[];
  assets: Asset[];
  assetPurchaseStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  assetAcceptRejectStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  lastAssetPurchase: PurchaseAssetResponse | null;
  lastAssetProcessed: AcceptRejectAssetResponse | null;

  // Sell Units
  sellRequests: SellRequest[];
  unitsSellStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  lastUnitsSell: SellUnitsResponse | null;
  sellRequestsPagination: Pagination;

  // Asset Revaluation
  assetRevaluationStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  lastAssetRevaluation: RevaluateAssetResponse | null;

  // Asset Revaluation Requests
  revaluationRequests: RevaluationRequest[];
  revaluationRequestsStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  revaluationAcceptRejectStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  lastRevaluationProcessed: AcceptRejectRevaluationResponse | null;

  // Landing Data
  landingData: LandingData[];
  landingDataStatus: 'idle' | 'pending' | 'succeeded' | 'failed';

  // NEW: Investor Landing Data
  investorLandingData: InvestorLandingDataResponse | null;
  investorLandingDataStatus: 'idle' | 'pending' | 'succeeded' | 'failed';

  // properties for contribution requests
  contributionRequests: ContributionRequest[];
  contributionRequestsStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
  contributionRequestsPagination: Pagination;

  // Common
  loading: boolean;
  error: string | null;
  unitsPagination: Pagination;
  assetsPagination: Pagination;
  assetRequestsPagination: Pagination;
  revaluationRequestsPagination: Pagination;
}

const initialPagination: Pagination = {
  totalCount: 0,
  currentPage: 1,
  pageSize: 10,
  totalPages: 1,
  hasMore: false,
  nextToken: null
};

const initialState: FundManagementInvestmentState = {
  purchaseRequests: [],
  unitsPurchaseStatus: 'idle',
  unitsAcceptRejectStatus: 'idle',
  lastUnitsPurchase: null,
  lastUnitsProcessed: null,

  assetRequests: [],
  assets: [],
  assetPurchaseStatus: 'idle',
  assetAcceptRejectStatus: 'idle',
  lastAssetPurchase: null,
  lastAssetProcessed: null,

  assetRevaluationStatus: 'idle',
  lastAssetRevaluation: null,

  revaluationRequests: [],
  revaluationRequestsStatus: 'idle',
  revaluationAcceptRejectStatus: 'idle',
  lastRevaluationProcessed: null,

  //  Initialize sell units state
  sellRequests: [],
  unitsSellStatus: 'idle',
  lastUnitsSell: null,
  sellRequestsPagination: { ...initialPagination },

  landingData: [],
  landingDataStatus: 'idle',

  // NEW: Initialize investor landing data
  investorLandingData: null,
  investorLandingDataStatus: 'idle',

  // initialize contribution requests state
  contributionRequests: [],
  contributionRequestsStatus: 'idle',
  contributionRequestsPagination: { ...initialPagination },

  loading: false,
  error: null,
  unitsPagination: { ...initialPagination },
  assetsPagination: { ...initialPagination },
  assetRequestsPagination: { ...initialPagination },
  revaluationRequestsPagination: { ...initialPagination }
};

// Async Thunks - Units Purchase
export const purchaseUnits = createAsyncThunk<
  PurchaseUnitsResponse,
  PurchaseUnitsPayload,
  { rejectValue: string }
>('fundManagementInvestment/purchaseUnits', async (purchaseData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(
      '/fund-investment-management/purchase-units',
      purchaseData
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to purchase units');
  }
});

export const getPurchaseUnitsRequests = createAsyncThunk<
  GetPurchaseRequestsResponse,
  GetPurchaseRequestsPayload,
  { rejectValue: string }
>(
  'fundManagementInvestment/getPurchaseUnitsRequests',
  async (requestParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/purchase-units-requests',
        requestParams
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch purchase requests');
    }
  }
);

export const acceptRejectUnitsRequest = createAsyncThunk<
  AcceptRejectUnitsResponse,
  AcceptRejectUnitsPayload,
  { rejectValue: string }
>('fundManagementInvestment/acceptRejectUnitsRequest', async (processData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(
      '/fund-investment-management/accept-reject-units-request',
      processData
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to process units request');
  }
});

// Sell Units Async Thunk
export const sellUnits = createAsyncThunk<
  SellUnitsResponse,
  SellUnitsPayload,
  { rejectValue: string }
>('fundManagementInvestment/sellUnits', async (sellData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/fund-investment-management/sell-units', sellData);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.error || error.response?.data?.message || 'Failed to sell units'
    );
  }
});

// Get Sell Units Requests Async Thunk
export const getSellUnitsRequests = createAsyncThunk<
  GetSellRequestsResponse,
  GetSellRequestsPayload,
  { rejectValue: string }
>('fundManagementInvestment/getSellUnitsRequests', async (requestParams, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(
      '/fund-investment-management/sell-units-requests',
      requestParams
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch sell requests');
  }
});

// Async Thunks - Asset Purchase
export const purchaseAsset = createAsyncThunk<
  PurchaseAssetResponse,
  PurchaseAssetPayload,
  { rejectValue: string }
>('fundManagementInvestment/purchaseAsset', async (assetData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(
      '/fund-investment-management/purchase-asset',
      assetData
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to purchase asset');
  }
});

export const getPurchaseAssetRequests = createAsyncThunk<
  GetAssetRequestsResponse,
  GetAssetRequestsPayload,
  { rejectValue: string }
>(
  'fundManagementInvestment/getPurchaseAssetRequests',
  async (requestParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/purchase-asset-requests',
        requestParams
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch asset requests');
    }
  }
);

export const acceptRejectAssetRequest = createAsyncThunk<
  AcceptRejectAssetResponse,
  AcceptRejectAssetPayload,
  { rejectValue: string }
>('fundManagementInvestment/acceptRejectAssetRequest', async (processData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(
      '/fund-investment-management/accept-reject-asset-request',
      processData
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to process asset request');
  }
});

export const getAllAssets = createAsyncThunk<
  GetAllAssetsResponse,
  GetAllAssetsPayload,
  { rejectValue: string }
>('fundManagementInvestment/getAllAssets', async (requestParams, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(
      '/fund-investment-management/all-assets-list',
      requestParams
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch assets');
  }
});

// Landing Data Async Thunk
export const getLandingData = createAsyncThunk<
  LandingData[],
  GetLandingDataPayload,
  { rejectValue: string }
>('fundManagementInvestment/getLandingData', async (requestParams, { rejectWithValue }) => {
  try {
    const payload: GetLandingDataPayload = {
      range: '3M',
      unitTypes: ['incomeUnits', 'growthUnits'],
      includeGraph: true,
      includeRecords: false,
      fromDateTime: null,
      toDateTime: null,
      ...requestParams
    };

    // Use request deduplication for landing data (cache for 3 minutes)
    const result = await requestCache.deduplicateRequest(
      'landingData',
      {
        groupId: payload.groupId,
        serviceId: payload.serviceId,
        isAdmin: payload.isAdmin,
        range: payload.range
      },
      async () => {
        const response = await axiosInstance.post(
          '/fund-investment-management/get-landing-data',
          payload,
          { timeout: 20000 } // 20 second timeout
        );
        return normalizeLandingDataResponse(response.data);
      }
    );

    return result;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch landing data');
  }
});

// NEW: Investor Landing Data Async Thunk
export const getInvestorLandingData = createAsyncThunk<
  InvestorLandingDataResponse,
  GetInvestorLandingDataPayload,
  { rejectValue: string }
>('fundManagementInvestment/getInvestorLandingData', async (requestParams, { rejectWithValue }) => {
  try {
    const payload: GetInvestorLandingDataPayload = {
      groupId: requestParams.groupId,
      serviceId: requestParams.serviceId,
      isAdmin: requestParams.isAdmin ?? false,
      range: requestParams.range ?? '3M',
      includeGraph: requestParams.includeGraph ?? false,
      includeRecords: requestParams.includeRecords ?? false,
      unitType: requestParams.unitType,
      unitTypes: requestParams.unitTypes,
      fromDate: requestParams.fromDate,
      toDate: requestParams.toDate,
      fromDateTime: requestParams.fromDateTime ?? null,
      toDateTime: requestParams.toDateTime ?? null,
      maxGraphPoints: requestParams.maxGraphPoints,
      ...(requestParams.investorsEmailId
        ? { investorsEmailId: requestParams.investorsEmailId }
        : {})
    };

    // Use request deduplication to prevent duplicate API calls
    const result = await requestCache.deduplicateRequest(
      'investorLandingData',
      {
        groupId: payload.groupId,
        serviceId: payload.serviceId,
        investorsEmailId: payload.investorsEmailId || '',
        isAdmin: payload.isAdmin,
        range: payload.range,
        includeGraph: payload.includeGraph,
        includeRecords: payload.includeRecords,
        unitType: payload.unitType || '',
        unitTypes: payload.unitTypes?.join(',') || ''
      },
      async () => {
        const response = await axiosInstance.get(
          '/fund-investment-management/investors-landing-data',
          {
            params: payload,
            timeout: 20000
          }
        );
        return normalizeInvestorLandingDataResponse(response.data);
      }
    );

    return result;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch investor landing data'
    );
  }
});

// Asset Revaluation Async Thunk
export const revaluateAsset = createAsyncThunk<
  RevaluateAssetResponse,
  RevaluateAssetPayload,
  { rejectValue: string }
>('fundManagementInvestment/revaluateAsset', async (revaluationData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(
      '/fund-investment-management/revaluation-asset',
      revaluationData
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to revaluate asset');
  }
});

// Asset Revaluation Request Async Thunk
export const getRevaluationAssetRequests = createAsyncThunk<
  GetRevaluationRequestsResponse,
  GetRevaluationRequestsPayload,
  { rejectValue: string }
>(
  'fundManagementInvestment/getRevaluationAssetRequests',
  async (requestParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/revaluation-asset-request',
        requestParams
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch revaluation requests'
      );
    }
  }
);

// Accept/Reject Revaluation Request Async Thunk
export const acceptRejectRevaluationRequest = createAsyncThunk<
  AcceptRejectRevaluationResponse,
  AcceptRejectRevaluationPayload,
  { rejectValue: string }
>(
  '/fund-investment-management/accept-reject-revaluation-asset-request',
  async (processData, { rejectWithValue }) => {
    console.log('Processing Revaluation Data:', processData); // Debugging log
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/accept-reject-revaluation-asset-request',
        processData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to process revaluation request'
      );
    }
  }
);

// Add the async thunk (after the existing thunks, around line 400)
export const getContributionRequests = createAsyncThunk<
  GetContributionRequestsResponse,
  GetContributionRequestsPayload,
  { rejectValue: string }
>(
  'fundManagementInvestment/getContributionRequests',
  async (requestParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/fund-investment-management/contribution-request',
        requestParams
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch contribution requests'
      );
    }
  }
);

// Create the slice
const fundManagementInvestmentSlice = createSlice({
  name: 'fundManagementInvestment',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetUnitsPurchaseStatus: (state) => {
      state.unitsPurchaseStatus = 'idle';
      state.lastUnitsPurchase = null;
    },
    resetUnitsAcceptRejectStatus: (state) => {
      state.unitsAcceptRejectStatus = 'idle';
      state.lastUnitsProcessed = null;
    },
    resetAssetPurchaseStatus: (state) => {
      state.assetPurchaseStatus = 'idle';
      state.lastAssetPurchase = null;
    },
    // Reset sell units status
    resetUnitsSellStatus: (state) => {
      state.unitsSellStatus = 'idle';
      state.lastUnitsSell = null;
    },
    resetAssetAcceptRejectStatus: (state) => {
      state.assetAcceptRejectStatus = 'idle';
      state.lastAssetProcessed = null;
    },
    resetAssetRevaluationStatus: (state) => {
      state.assetRevaluationStatus = 'idle';
      state.lastAssetRevaluation = null;
    },
    resetRevaluationRequestsStatus: (state) => {
      state.revaluationRequestsStatus = 'idle';
      state.revaluationRequests = [];
    },
    resetRevaluationAcceptRejectStatus: (state) => {
      state.revaluationAcceptRejectStatus = 'idle';
      state.lastRevaluationProcessed = null;
    },

    resetLandingDataStatus: (state) => {
      state.landingDataStatus = 'idle';
      state.landingData = [];
    },
    // NEW: Reset contribution requests status
    resetContributionRequestsStatus: (state) => {
      state.contributionRequestsStatus = 'idle';
      state.contributionRequests = [];
    },

    clearContributionRequests: (state) => {
      state.contributionRequests = [];
      state.contributionRequestsPagination = { ...initialPagination };
    },
    // NEW: Reset investor landing data status
    resetInvestorLandingDataStatus: (state) => {
      state.investorLandingDataStatus = 'idle';
      state.investorLandingData = null;
    },
    updatePurchaseRequestStatus: (state, action) => {
      const { requestId, status, lastUpdatedBy, lastUpdatedDateTime } = action.payload;
      const request = state.purchaseRequests.find((req) => req.requestId === requestId);
      if (request) {
        request.status = status;
        request.lastUpdatedBy = lastUpdatedBy;
        request.lastUpdatedDateTime = lastUpdatedDateTime;
      }
    },
    //  Update sell request status
    updateSellRequestStatus: (state, action) => {
      const { requestId, status, lastUpdatedBy, lastUpdatedDateTime } = action.payload;
      const request = state.sellRequests.find((req) => req.requestId === requestId);
      if (request) {
        request.status = status;
        request.lastUpdatedBy = lastUpdatedBy;
        request.lastUpdatedDateTime = lastUpdatedDateTime;
      }
    },
    updateAssetRequestStatus: (state, action) => {
      const { requestId, status, lastUpdatedBy, lastUpdatedDateTime } = action.payload;
      const request = state.assetRequests.find((req) => req.requestId === requestId);
      if (request) {
        request.status = status;
        request.lastUpdatedBy = lastUpdatedBy;
        request.lastUpdatedDateTime = lastUpdatedDateTime;
      }
    },
    updateRevaluationRequestStatus: (state, action) => {
      const { requestId, status, lastUpdatedBy, lastUpdatedDate } = action.payload;
      const request = state.revaluationRequests.find((req) => req.requestId === requestId);
      if (request) {
        request.status = status;
        request.lastUpdatedBy = lastUpdatedBy;
        request.lastUpdatedDate = lastUpdatedDate;
      }
    },
    clearPurchaseRequests: (state) => {
      state.purchaseRequests = [];
      state.unitsPagination = { ...initialPagination };
    },
    clearAssetRequests: (state) => {
      state.assetRequests = [];
      state.assetRequestsPagination = { ...initialPagination };
    },
    clearRevaluationRequests: (state) => {
      state.revaluationRequests = [];
      state.revaluationRequestsPagination = { ...initialPagination };
    },
    //Clear sell requests
    clearSellRequests: (state) => {
      state.sellRequests = [];
      state.sellRequestsPagination = { ...initialPagination };
    },
    clearAssets: (state) => {
      state.assets = [];
      state.assetsPagination = { ...initialPagination };
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    clearFundManagementState: (state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      // Units Purchase Cases
      .addCase(purchaseUnits.pending, (state) => {
        state.loading = true;
        state.unitsPurchaseStatus = 'pending';
        state.error = null;
      })
      .addCase(purchaseUnits.fulfilled, (state, action) => {
        state.loading = false;
        state.unitsPurchaseStatus = 'succeeded';
        state.lastUnitsPurchase = action.payload;
        state.error = null;
      })
      .addCase(purchaseUnits.rejected, (state, action) => {
        state.loading = false;
        state.unitsPurchaseStatus = 'failed';
        state.error = action.payload || 'An error occurred during units purchase';
      })

      // Get Purchase Units Requests Cases
      .addCase(getPurchaseUnitsRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPurchaseUnitsRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.purchaseRequests = action.payload.purchaseRequests || [];
        state.unitsPagination = {
          totalCount: action.payload.totalCount || 0,
          currentPage: action.payload.currentPage || 1,
          pageSize: action.payload.pageSize || 10,
          totalPages: action.payload.totalPages || 1,
          hasMore: action.payload.hasMore || false,
          nextToken: action.payload.nextToken || null
        };
        state.error = null;
      })
      .addCase(getPurchaseUnitsRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred while fetching purchase requests';
      })

      // Accept/Reject Units Request Cases
      .addCase(acceptRejectUnitsRequest.pending, (state) => {
        state.loading = true;
        state.unitsAcceptRejectStatus = 'pending';
        state.error = null;
      })
      .addCase(acceptRejectUnitsRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.unitsAcceptRejectStatus = 'succeeded';
        state.lastUnitsProcessed = action.payload;
        state.error = null;
      })
      .addCase(acceptRejectUnitsRequest.rejected, (state, action) => {
        state.loading = false;
        state.unitsAcceptRejectStatus = 'failed';
        state.error = action.payload || 'An error occurred while processing units request';
      })

      // NEW: Sell Units Cases
      .addCase(sellUnits.pending, (state) => {
        state.loading = true;
        state.unitsSellStatus = 'pending';
        state.error = null;
      })
      .addCase(sellUnits.fulfilled, (state, action) => {
        state.loading = false;
        state.unitsSellStatus = 'succeeded';
        state.lastUnitsSell = action.payload;
        state.error = null;
      })
      .addCase(sellUnits.rejected, (state, action) => {
        state.loading = false;
        state.unitsSellStatus = 'failed';
        state.error = action.payload || 'An error occurred during units sell';
      })

      //Get Sell Units Requests Cases
      .addCase(getSellUnitsRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSellUnitsRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.sellRequests = action.payload.sellRequests || [];
        state.sellRequestsPagination = {
          totalCount: action.payload.totalCount || 0,
          currentPage: action.payload.currentPage || 1,
          pageSize: action.payload.pageSize || 25,
          totalPages: action.payload.totalPages || 1,
          hasMore: action.payload.hasMore || false,
          nextToken: action.payload.nextToken || null
        };
        state.error = null;
      })
      .addCase(getSellUnitsRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred while fetching sell requests';
      })

      // Asset Purchase Cases
      .addCase(purchaseAsset.pending, (state) => {
        state.loading = true;
        state.assetPurchaseStatus = 'pending';
        state.error = null;
      })
      .addCase(purchaseAsset.fulfilled, (state, action) => {
        state.loading = false;
        state.assetPurchaseStatus = 'succeeded';
        state.lastAssetPurchase = action.payload;
        state.error = null;
      })
      .addCase(purchaseAsset.rejected, (state, action) => {
        state.loading = false;
        state.assetPurchaseStatus = 'failed';
        state.error = action.payload || 'An error occurred during asset purchase';
      })

      // Get Asset Requests Cases
      .addCase(getPurchaseAssetRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPurchaseAssetRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.assetRequests = action.payload.purchaseAssetRequests || [];
        state.assetRequestsPagination = {
          totalCount: action.payload.totalCount || 0,
          currentPage: action.payload.currentPage || 1,
          pageSize: action.payload.pageSize || 10,
          totalPages: action.payload.totalPages || 1,
          hasMore: action.payload.hasMore || false,
          nextToken: action.payload.nextToken || null
        };
        state.error = null;
      })
      .addCase(getPurchaseAssetRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred while fetching asset requests';
      })

      // Accept/Reject Asset Request Cases
      .addCase(acceptRejectAssetRequest.pending, (state) => {
        state.loading = true;
        state.assetAcceptRejectStatus = 'pending';
        state.error = null;
      })
      .addCase(acceptRejectAssetRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.assetAcceptRejectStatus = 'succeeded';
        state.lastAssetProcessed = action.payload;
        state.error = null;
      })
      .addCase(acceptRejectAssetRequest.rejected, (state, action) => {
        state.loading = false;
        state.assetAcceptRejectStatus = 'failed';
        state.error = action.payload || 'An error occurred while processing asset request';
      })

      // Get All Assets Cases
      .addCase(getAllAssets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllAssets.fulfilled, (state, action) => {
        state.loading = false;
        state.assets = action.payload.assets || [];
        state.assetsPagination = {
          totalCount: action.payload.totalCount || 0,
          currentPage: action.payload.currentPage || 1,
          pageSize: action.payload.pageSize || 10,
          totalPages: action.payload.totalPages || 1,
          hasMore: action.payload.hasMore || false,
          nextToken: action.payload.nextToken || null
        };
        state.error = null;
      })
      .addCase(getAllAssets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred while fetching assets';
      })

      // Landing Data Cases
      .addCase(getLandingData.pending, (state) => {
        state.loading = true;
        state.landingDataStatus = 'pending';
        state.error = null;
      })
      .addCase(getLandingData.fulfilled, (state, action) => {
        state.loading = false;
        state.landingDataStatus = 'succeeded';
        state.landingData = action.payload || [];
        state.error = null;
      })
      .addCase(getLandingData.rejected, (state, action) => {
        state.loading = false;
        state.landingDataStatus = 'failed';
        state.error = action.payload || 'An error occurred while fetching landing data';
      })

      // NEW: Investor Landing Data Cases
      .addCase(getInvestorLandingData.pending, (state) => {
        state.loading = true;
        state.investorLandingDataStatus = 'pending';
        state.error = null;
      })
      .addCase(getInvestorLandingData.fulfilled, (state, action) => {
        state.loading = false;
        state.investorLandingDataStatus = 'succeeded';
        state.investorLandingData = action.payload;
        state.error = null;
      })
      .addCase(getInvestorLandingData.rejected, (state, action) => {
        state.loading = false;
        state.investorLandingDataStatus = 'failed';
        state.error = action.payload || 'An error occurred while fetching investor landing data';
      })

      // Asset Revaluation Cases
      .addCase(revaluateAsset.pending, (state) => {
        state.loading = true;
        state.assetRevaluationStatus = 'pending';
        state.error = null;
      })
      .addCase(revaluateAsset.fulfilled, (state, action) => {
        state.loading = false;
        state.assetRevaluationStatus = 'succeeded';
        state.lastAssetRevaluation = action.payload;
        state.error = null;
      })
      .addCase(revaluateAsset.rejected, (state, action) => {
        state.loading = false;
        state.assetRevaluationStatus = 'failed';
        state.error = action.payload || 'An error occurred during asset revaluation';
      })

      // Asset Revaluation Request Cases
      .addCase(getRevaluationAssetRequests.pending, (state) => {
        state.loading = true;
        state.revaluationRequestsStatus = 'pending';
        state.error = null;
      })
      .addCase(getRevaluationAssetRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.revaluationRequestsStatus = 'succeeded';
        state.revaluationRequests = action.payload.revaluationAssetRequests || [];
        state.revaluationRequestsPagination = {
          totalCount: action.payload.totalCount || 0,
          currentPage: action.payload.currentPage || 1,
          pageSize: action.payload.pageSize || 10,
          totalPages: action.payload.totalPages || 1,
          hasMore: action.payload.hasMore || false,
          nextToken: action.payload.nextToken || null
        };
        state.error = null;
      })
      .addCase(getRevaluationAssetRequests.rejected, (state, action) => {
        state.loading = false;
        state.revaluationRequestsStatus = 'failed';
        state.error = action.payload || 'An error occurred while fetching revaluation requests';
      })

      // Accept/Reject Revaluation Request Cases
      .addCase(acceptRejectRevaluationRequest.pending, (state) => {
        state.loading = true;
        state.revaluationAcceptRejectStatus = 'pending';
        state.error = null;
      })
      .addCase(acceptRejectRevaluationRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.revaluationAcceptRejectStatus = 'succeeded';
        state.lastRevaluationProcessed = action.payload;
        state.error = null;
      })
      .addCase(acceptRejectRevaluationRequest.rejected, (state, action) => {
        state.loading = false;
        state.revaluationAcceptRejectStatus = 'failed';
        state.error = action.payload || 'An error occurred while processing revaluation request';
      })

      // Contribution Requests Cases
      .addCase(getContributionRequests.pending, (state) => {
        state.loading = true;
        state.contributionRequestsStatus = 'pending';
        state.error = null;
      })
      .addCase(getContributionRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.contributionRequestsStatus = 'succeeded';
        state.contributionRequests = action.payload.contributionRequests || [];
        state.contributionRequestsPagination = {
          totalCount: action.payload.totalCount || 0,
          currentPage: action.payload.currentPage || 1,
          pageSize: action.payload.pageSize || 10,
          totalPages: action.payload.totalPages || 1,
          hasMore: action.payload.hasMore || false,
          nextToken: action.payload.nextToken || null
        };
        state.error = null;
      })
      .addCase(getContributionRequests.rejected, (state, action) => {
        state.loading = false;
        state.contributionRequestsStatus = 'failed';
        state.error = action.payload || 'An error occurred while fetching contribution requests';
      });
  }
});

// Export actions
export const {
  clearError,
  resetUnitsPurchaseStatus,
  resetUnitsSellStatus, // NEW
  resetUnitsAcceptRejectStatus,
  resetAssetPurchaseStatus,
  resetAssetAcceptRejectStatus,
  resetAssetRevaluationStatus,
  resetRevaluationRequestsStatus,
  resetRevaluationAcceptRejectStatus,
  resetLandingDataStatus,
  resetInvestorLandingDataStatus, // NEW
  updatePurchaseRequestStatus,
  updateAssetRequestStatus,
  updateRevaluationRequestStatus,
  updateSellRequestStatus, // NEW

  //
  resetContributionRequestsStatus,
  clearContributionRequests,
  //
  clearPurchaseRequests,
  clearAssetRequests,
  clearSellRequests, // NEW
  clearRevaluationRequests,
  clearAssets,
  setLoading,
  clearFundManagementState
} = fundManagementInvestmentSlice.actions;

// Export reducer
export default fundManagementInvestmentSlice.reducer;

// Selectors
export const selectFundManagementInvestment = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment;

export const selectPurchaseRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.purchaseRequests;

// NEW: Sell Units Selectors
export const selectUnitsSellStatus = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.unitsSellStatus;

export const selectLastUnitsSell = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.lastUnitsSell;

export const selectSellRequestsPagination = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.sellRequestsPagination;

export const selectPendingSellRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.sellRequests.filter((request) => request.status === 'pending');

export const selectApprovedSellRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.sellRequests.filter((request) => request.status === 'approved');

export const selectRejectedSellRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.sellRequests.filter((request) => request.status === 'rejected');

export const selectSellRequestByRequestId = (
  state: { fundManagementInvestment: FundManagementInvestmentState },
  requestId: string
) => state.fundManagementInvestment.sellRequests.find((request) => request.requestId === requestId);

export const selectAssetRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.assetRequests;

export const selectAssets = (state: { fundManagementInvestment: FundManagementInvestmentState }) =>
  state.fundManagementInvestment.assets;

// Revaluation requests selectors
export const selectRevaluationRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.revaluationRequests;

export const selectRevaluationRequestsStatus = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.revaluationRequestsStatus;

export const selectRevaluationAcceptRejectStatus = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.revaluationAcceptRejectStatus;

export const selectRevaluationRequestsPagination = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.revaluationRequestsPagination;

export const selectLoading = (state: { fundManagementInvestment: FundManagementInvestmentState }) =>
  state.fundManagementInvestment.loading;

export const selectError = (state: { fundManagementInvestment: FundManagementInvestmentState }) =>
  state.fundManagementInvestment.error;

export const selectUnitsPurchaseStatus = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.unitsPurchaseStatus;

export const selectUnitsAcceptRejectStatus = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.unitsAcceptRejectStatus;

export const selectAssetPurchaseStatus = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.assetPurchaseStatus;

export const selectAssetAcceptRejectStatus = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.assetAcceptRejectStatus;

// Asset Revaluation Selectors
export const selectAssetRevaluationStatus = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.assetRevaluationStatus;

export const selectLastAssetRevaluation = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.lastAssetRevaluation;

// Landing Data Selectors
export const selectLandingData = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.landingData;

export const selectLandingDataStatus = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.landingDataStatus;

// NEW: Investor Landing Data Selectors
export const selectInvestorLandingData = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.investorLandingData;

export const selectInvestorLandingDataStatus = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.investorLandingDataStatus;

export const selectInvestorIncomeUnitsData = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.investorLandingData?.incomeUnits;

export const selectInvestorGrowthUnitsData = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.investorLandingData?.growthUnits;

export const selectIncomeUnitsData = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.landingData.find((data) => data.unitType === 'incomeUnits');

export const selectGrowthUnitsData = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.landingData.find((data) => data.unitType === 'growthUnits');

export const selectUnitsPagination = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.unitsPagination;

export const selectAssetsPagination = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.assetsPagination;

export const selectAssetRequestsPagination = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.assetRequestsPagination;

export const selectLastUnitsPurchase = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.lastUnitsPurchase;

export const selectLastUnitsProcessed = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.lastUnitsProcessed;

export const selectLastAssetPurchase = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.lastAssetPurchase;

export const selectLastAssetProcessed = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.lastAssetProcessed;

export const selectLastRevaluationProcessed = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.lastRevaluationProcessed;

// Contribution Requests Selectors
export const selectContributionRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.contributionRequests;

export const selectContributionRequestsStatus = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.contributionRequestsStatus;

export const selectContributionRequestsPagination = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => state.fundManagementInvestment.contributionRequestsPagination;

export const selectPendingContributionRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.contributionRequests.filter(
    (request) => request.status === 'pending'
  );

export const selectAcceptedContributionRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.contributionRequests.filter(
    (request) => request.status === 'accepted'
  );

export const selectRejectedContributionRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.contributionRequests.filter(
    (request) => request.status === 'rejected'
  );

// Computed Selectors
export const selectPendingUnitsRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.purchaseRequests.filter((request) => request.status === 'pending');

export const selectAcceptedUnitsRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.purchaseRequests.filter(
    (request) => request.status === 'accepted'
  );

export const selectRejectedUnitsRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.purchaseRequests.filter(
    (request) => request.status === 'rejected'
  );

export const selectPendingAssetRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.assetRequests.filter((request) => request.status === 'pending');

export const selectAcceptedAssetRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.assetRequests.filter((request) => request.status === 'accepted');

export const selectRejectedAssetRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.assetRequests.filter((request) => request.status === 'rejected');

// Revaluation request computed selectors
export const selectPendingRevaluationRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.revaluationRequests.filter(
    (request) => request.status === 'pending'
  );

export const selectAcceptedRevaluationRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.revaluationRequests.filter(
    (request) => request.status === 'accepted'
  );

export const selectRejectedRevaluationRequests = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.revaluationRequests.filter(
    (request) => request.status === 'rejected'
  );

export const selectRequestsByInvestor = (
  state: { fundManagementInvestment: FundManagementInvestmentState },
  investorEmailId: string
) =>
  state.fundManagementInvestment.purchaseRequests.filter(
    (request) => request.investorEmailId === investorEmailId
  );

export const selectRequestsByUnitType = (
  state: { fundManagementInvestment: FundManagementInvestmentState },
  unitType: string
) =>
  state.fundManagementInvestment.purchaseRequests.filter(
    (request) => request.unitType === unitType
  );

export const selectAssetsByType = (
  state: { fundManagementInvestment: FundManagementInvestmentState },
  assetType: string
) => state.fundManagementInvestment.assets.filter((asset) => asset.type === assetType);

export const selectTotalInvestmentAmount = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.purchaseRequests.reduce(
    (total, request) => total + parseFloat(request.investedAmount || '0'),
    0
  );

export const selectTotalUnits = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.purchaseRequests.reduce(
    (total, request) => total + parseFloat(request.investedUnits || '0'),
    0
  );

export const selectTotalAssetValue = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.assets.reduce(
    (total, asset) => total + parseFloat(asset.assetData.purchaseValue || '0'),
    0
  );

export const selectTotalCashUsed = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) =>
  state.fundManagementInvestment.assets.reduce(
    (total, asset) => total + parseFloat(asset.cashUsed || '0'),
    0
  );

export const selectHasPendingOperations = (state: {
  fundManagementInvestment: FundManagementInvestmentState;
}) => {
  const {
    unitsPurchaseStatus,
    unitsAcceptRejectStatus,
    assetPurchaseStatus,
    assetAcceptRejectStatus,
    assetRevaluationStatus,
    revaluationRequestsStatus,
    revaluationAcceptRejectStatus,
    landingDataStatus,
    investorLandingDataStatus, // NEW
    loading
  } = state.fundManagementInvestment;
  return (
    loading ||
    unitsPurchaseStatus === 'pending' ||
    unitsAcceptRejectStatus === 'pending' ||
    assetPurchaseStatus === 'pending' ||
    assetAcceptRejectStatus === 'pending' ||
    assetRevaluationStatus === 'pending' ||
    revaluationRequestsStatus === 'pending' ||
    revaluationAcceptRejectStatus === 'pending' ||
    landingDataStatus === 'pending' ||
    investorLandingDataStatus === 'pending' // NEW
  );
};

export const selectRequestByRequestId = (
  state: { fundManagementInvestment: FundManagementInvestmentState },
  requestId: string
) =>
  state.fundManagementInvestment.purchaseRequests.find(
    (request) => request.requestId === requestId
  );

export const selectAssetRequestByRequestId = (
  state: { fundManagementInvestment: FundManagementInvestmentState },
  requestId: string
) =>
  state.fundManagementInvestment.assetRequests.find((request) => request.requestId === requestId);

export const selectRevaluationRequestByRequestId = (
  state: { fundManagementInvestment: FundManagementInvestmentState },
  requestId: string
) =>
  state.fundManagementInvestment.revaluationRequests.find(
    (request) => request.requestId === requestId
  );

export const selectAssetByAssetId = (
  state: { fundManagementInvestment: FundManagementInvestmentState },
  assetId: string
) => state.fundManagementInvestment.assets.find((asset) => asset.assetId === assetId);

// Export types for use in components
export type {
  AcceptRejectAssetPayload,
  AcceptRejectAssetResponse,
  AcceptRejectRevaluationPayload,
  AcceptRejectRevaluationResponse,
  AcceptRejectUnitsPayload,
  AcceptRejectUnitsResponse,
  Asset,
  AssetData,
  AssetRequest,

  // NEW: Export contribution requests types
  ContributionRequest,
  FundManagementInvestmentState,
  GetAllAssetsPayload,
  GetAllAssetsResponse,
  GetAssetRequestsPayload,
  GetAssetRequestsResponse,
  GetContributionRequestsPayload,
  GetContributionRequestsResponse,
  GetInvestorLandingDataPayload,
  GetLandingDataPayload,
  GetPurchaseRequestsPayload,
  GetPurchaseRequestsResponse,
  GetRevaluationRequestsPayload,
  GetRevaluationRequestsResponse, GetSellRequestsPayload,
  GetSellRequestsResponse, InvestorLandingDataResponse,
  // NEW: Export investor landing data types
  InvestorUnitsData,
  LandingData,
  Liability,
  MonthlyExpenses,
  MonthlyIncome,
  PurchaseAssetPayload,
  PurchaseAssetResponse,
  PurchaseRequest,
  PurchaseUnitsPayload,
  PurchaseUnitsResponse,
  RevaluateAssetPayload,
  RevaluateAssetResponse,
  RevaluationAssetData,
  RevaluationRequest, SellRequest,
  //
  SellUnitsPayload,
  SellUnitsResponse, UpdatedMonthlyExpenses,
  UpdatedMonthlyIncome
};

